import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';

import { useAxios } from '@/hooks/useAxios';
import type { SavedQuery } from '@/sections/insights/queryBuilder/types/SavedQuery';
import { UniqueQueryKeys } from '@/types';

interface UseSaveQueryOptions {
  onSuccess?: () => void;
}

/**
 * Hook for saving queries to user settings
 *
 * Handles:
 * - Duplicate ID checking via GET /my
 * - Confirmation modal for existing queries
 * - Save via PUT /setting
 * - Cache invalidation after save
 * - Success/error notifications
 */
export function useSaveQuery(options?: UseSaveQueryOptions) {
  const axios = useAxios({});
  const queryClient = useQueryClient();
  const [isSaving, setIsSaving] = useState(false);

  const saveMutation = useMutation({
    mutationFn: async (query: SavedQuery) => {
      // PUT /setting with name format: saved_query#<id>
      await axios.put('/setting', {
        name: `saved_query#${query.id}`,
        value: query, // Nested object, NOT JSON string
      });
    },
    onSuccess: async () => {
      // Invalidate and immediately refetch to update Custom Queries folder
      // Using refetchQueries ensures immediate UI update without manual page refresh
      await queryClient.refetchQueries({
        queryKey: [UniqueQueryKeys.MY, 'setting'],
        type: 'active', // Only refetch queries that have active observers
      });
      toast.success(
        "Query saved successfully. You may now view it from 'Custom Queries'"
      );
      options?.onSuccess?.();
    },
    onError: error => {
      toast.error('Failed to save query');
      console.error('Save query error:', error);
    },
  });

  const save = async (query: SavedQuery) => {
    setIsSaving(true);

    try {
      // Check if query ID already exists
      const existingResponse = await axios.get('/my', {
        params: {
          label: 'setting',
          key: `#setting#saved_query#${query.id}`,
          exact: 'true',
        },
      });

      const exists = existingResponse.data?.settings?.length > 0;

      if (exists) {
        // TODO: Show confirmation modal
        // For now, proceed with save
        console.warn('Query ID exists, overwriting');
      }

      await saveMutation.mutateAsync(query);
    } catch (error) {
      toast.error('Failed to check existing query');
      console.error('Check existing query error:', error);
    } finally {
      setIsSaving(false);
    }
  };

  return {
    save,
    isSaving,
  };
}
